import React, { useEffect, useState } from "react";
import {
  Box,
  Paper,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  IconButton,
  Tooltip,
  Typography,
  CircularProgress,
  Alert,
  Snackbar,
  Pagination,
  Stack,
  Autocomplete,
  TextField,
} from "@mui/material";
import {
  RefreshOutlined,
  EditOutlined,
  DeleteOutlineRounded,
  ArticleOutlined,
} from "@mui/icons-material";
import { keyframes } from "@mui/system";
import { adminService } from "../services/adminService";
import NotificationEditDialog from "../components/NotificationEditDialog";
import NotificationDescDialog from "../components/NotificationDescDialog";
import { textPrimary, textSecondary } from "../../../constants/consts";

const fadeInUp = keyframes`
  from { opacity: 0; transform: translateY(14px); }
  to   { opacity: 1; transform: translateY(0); }
`;

const PAGE_SIZE = 13;

const USER_TYPES = [
  { value: "all", label: "Herkes" },
  { value: "user", label: "Kullanıcı" },
  { value: "rootUser", label: "Admin Müşteri" },
];

const userTypeLabel = (v) => USER_TYPES.find((t) => t.value === v)?.label || v;

const normalize = (s = "") => String(s).toLocaleLowerCase("tr-TR").trim();

// function formatDate(iso) {
//   if (!iso) return "-";
//   try {
//     const d = new Date(iso);
//     if (isNaN(d.getTime())) return iso;
//     const p = (n) => String(n).padStart(2, "0");
//     return `${p(d.getDate())}/${p(d.getMonth() + 1)}/${d.getFullYear()} ${p(d.getHours())}:${p(d.getMinutes())}`;
//   } catch {
//     return iso;
//   }
// }
// Timezone bilgisi yoksa UTC kabul ederek Date objesi döner
function parseDate(iso) {
  if (!iso) return null;
  try {
    let str = String(iso).trim();
    // Z / +03:00 / GMT / UTC hepsini yakalar
    const hasTz = /Z$|[+-]\d{2}:?\d{2}$|\b(GMT|UTC)\b/i.test(str);
    if (!hasTz) str = str.replace(" ", "T") + "Z";
    const d = new Date(str);
    return isNaN(d.getTime()) ? null : d;
  } catch {
    return null;
  }
}

// GMT'siz, gg/aa/yyyy ss:dd
function formatDate(iso) {
  const d = parseDate(iso);
  if (!d) return iso || "-";
  const p = (n) => String(n).padStart(2, "0");
  return `${p(d.getDate())}/${p(d.getMonth() + 1)}/${d.getFullYear()} ${p(d.getHours())}:${p(d.getMinutes())}`;
}
export default function AdminNotifications() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState("");
  const [page, setPage] = useState(1);

  const [sunucuKoduList, setSunucuKoduList] = useState([]);
  const [sunucuFilter, setSunucuFilter] = useState("Hepsi");
  const [userTypeFilter, setUserTypeFilter] = useState("all");

  const [editDialog, setEditDialog] = useState({ open: false, item: null });
  const [descDialog, setDescDialog] = useState({
    open: false,
    title: "",
    desc: "",
  });

  const [snack, setSnack] = useState({
    open: false,
    msg: "",
    severity: "info",
  });

  /* ─── Sunucu kodlarını yükle ─── */
  useEffect(() => {
    (async () => {
      try {
        const companies = await adminService.getCompanies();
        const list = [
          "Hepsi",
          ...companies.map((c) => c.sunucu_kodu).filter(Boolean),
        ];
        setSunucuKoduList(list);
      } catch (e) {
        console.warn(e);
      }
    })();
  }, []);

  /* ─── Veri yükleme ─── */
  const loadData = async (silent = false) => {
    if (silent) setRefreshing(true);
    else setLoading(true);
    setError("");
    try {
      const data = await adminService.getNotifications({
        sunucuKodu: sunucuFilter,
        userType: userTypeFilter,
      });
      const mapped = data.map((n) => ({
        id: String(n.id),
        title: n.title ?? "-",
        description: n.description ?? "",
        image: n.img ?? n.image ?? "",
        sunucuKodu: n.sunucu_kodu ?? "",
        userType: n.user_type ?? "all",
        createdAt: n.created_at,
      }));
      setRows(mapped);
      setPage(1);
    } catch (e) {
      console.error(e);
      setError("Duyurular yüklenemedi.");
    } finally {
      if (silent) setRefreshing(false);
      else setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
    // eslint-disable-next-line
  }, [sunucuFilter, userTypeFilter]);

  /* ─── Sayfalama ─── */
  const totalPages = Math.max(1, Math.ceil(rows.length / PAGE_SIZE));
  const safePage = Math.min(page, totalPages);
  const pageRows = rows.slice((safePage - 1) * PAGE_SIZE, safePage * PAGE_SIZE);

  /* ─── Silme ─── */
  const handleDelete = async (row) => {
    if (!window.confirm(`"${row.title}" silinsin mi?`)) return;
    try {
      await adminService.deleteNotification(row.id);
      setSnack({ open: true, msg: "Duyuru silindi.", severity: "success" });
      await loadData(true);
    } catch (e) {
      console.error(e);
      setSnack({ open: true, msg: "Silme başarısız.", severity: "error" });
    }
  };

  /* ─── Render ─── */
  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        height: "100%",
        minHeight: 0,
        gap: 1.5,
      }}
    >
      {/* Başlık */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1,
          flexShrink: 0,
          flexWrap: "wrap",
          animation: `${fadeInUp} 0.4s ease-out both`,
        }}
      >
        <Typography
          variant="h6"
          fontWeight={800}
          color="#fff"
          sx={{ fontSize: 18 }}
        >
          Duyurular
        </Typography>
        <Box
          sx={{
            px: 1.25,
            py: 0.4,
            borderRadius: 5,
            backgroundColor: "rgba(255,255,255,0.14)",
            color: "#fff",
            fontWeight: 700,
            fontSize: 12,
            border: "1px solid rgba(255,255,255,0.18)",
          }}
        >
          {rows.length} kayıt
        </Box>

        <Tooltip title="Yenile">
          <span>
            <IconButton
              size="small"
              onClick={() => loadData(true)}
              disabled={loading || refreshing}
              sx={{
                ml: "auto",
                color: "#fff",
                backgroundColor: "rgba(255,255,255,0.12)",
                border: "1px solid rgba(255,255,255,0.18)",
                "&:hover": { backgroundColor: "rgba(255,255,255,0.22)" },
              }}
            >
              <RefreshOutlined
                sx={{
                  fontSize: 18,
                  animation:
                    loading || refreshing
                      ? "spin 1.2s linear infinite"
                      : "none",
                  "@keyframes spin": { to: { transform: "rotate(360deg)" } },
                }}
              />
            </IconButton>
          </span>
        </Tooltip>
      </Box>

      {/* Filtreler */}
      <Stack
        direction={{ xs: "column", sm: "row" }}
        spacing={1.5}
        sx={{ flexShrink: 0 }}
      >
        <Autocomplete
          size="small"
          options={sunucuKoduList}
          value={sunucuFilter}
          onChange={(_, v) => setSunucuFilter(v || "Hepsi")}
          disableClearable
          filterOptions={(opts, { inputValue }) =>
            opts.filter((o) => normalize(o).includes(normalize(inputValue)))
          }
          sx={{ minWidth: 220 }}
          renderInput={(params) => (
            <TextField
              {...params}
              placeholder="Sunucu kodu..."
              sx={{
                "& .MuiOutlinedInput-root": {
                  backgroundColor: "rgba(255,255,255,0.94)",
                  borderRadius: 2,
                },
              }}
            />
          )}
        />

        <Autocomplete
          size="small"
          options={USER_TYPES.map((t) => t.value)}
          value={userTypeFilter}
          onChange={(_, v) => setUserTypeFilter(v || "all")}
          getOptionLabel={userTypeLabel}
          disableClearable
          renderOption={(props, opt) => (
            <li {...props}>{userTypeLabel(opt)}</li>
          )}
          sx={{ minWidth: 200 }}
          renderInput={(params) => (
            <TextField
              {...params}
              placeholder="Kullanıcı tipi"
              sx={{
                "& .MuiOutlinedInput-root": {
                  backgroundColor: "rgba(255,255,255,0.94)",
                  borderRadius: 2,
                },
              }}
            />
          )}
        />
      </Stack>

      {error && (
        <Alert severity="error" sx={{ borderRadius: 2 }}>
          {error}
        </Alert>
      )}

      {loading ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
          <CircularProgress sx={{ color: "#fff" }} />
        </Box>
      ) : (
        <>
          <Paper
            elevation={0}
            sx={{
              flex: 1,
              minHeight: 0,
              overflow: "hidden",
              borderRadius: 2.5,
              display: "flex",
              flexDirection: "column",
              backgroundColor: "rgba(255,255,255,0.98)",
              boxShadow: "0 12px 32px rgba(0,0,0,0.20)",
              border: "1px solid rgba(255,255,255,0.30)",
              animation: `${fadeInUp} 0.5s cubic-bezier(0.22, 1, 0.36, 1) 0.1s backwards`,
            }}
          >
            <Box
              sx={{
                flex: 1,
                minHeight: 0,
                overflowY: "auto",
                "&::-webkit-scrollbar": { width: 6, height: 6 },
                "&::-webkit-scrollbar-thumb": {
                  backgroundColor: "rgba(15,50,65,0.20)",
                  borderRadius: 3,
                },
              }}
            >
              <Table stickyHeader size="small">
                <TableHead>
                  <TableRow
                    sx={{
                      "& th": {
                        background:
                          "linear-gradient(180deg, #F1F5F9 0%, #E2E8F0 100%)",
                        color: "#334155",
                        fontWeight: 800,
                        fontSize: 10.5,
                        textTransform: "uppercase",
                        letterSpacing: "0.04em",
                        py: 1,
                        px: 1.5,
                        borderBottom: "1px solid #CBD5E1",
                        whiteSpace: "nowrap",
                      },
                    }}
                  >
                    <TableCell>Foto</TableCell>
                    <TableCell>Konu</TableCell>
                    <TableCell>Duyuru</TableCell>
                    <TableCell>Sunucu</TableCell>
                    <TableCell>Kime</TableCell>
                    <TableCell>Tarih</TableCell>
                    <TableCell align="right">İşlem</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {pageRows.length === 0 ? (
                    <TableRow>
                      <TableCell
                        colSpan={7}
                        sx={{
                          textAlign: "center",
                          py: 6,
                          color: textSecondary,
                          fontSize: 13,
                        }}
                      >
                        Kayıt bulunamadı
                      </TableCell>
                    </TableRow>
                  ) : (
                    pageRows.map((r, i) => (
                      <TableRow
                        key={r.id}
                        sx={{
                          backgroundColor: i % 2 ? "#F8FAFC" : "#fff",
                          "&:hover": {
                            backgroundColor: "rgba(4,87,129,0.06)",
                          },
                          transition: "background-color 0.2s ease",
                          "& td": {
                            py: 0.75,
                            px: 1.5,
                            fontSize: 12.5,
                            color: "#334155",
                            borderBottom: "1px solid #F1F5F9",
                          },
                        }}
                      >
                        <TableCell>
                          {r.image ? (
                            <Box
                              component="img"
                              src={r.image}
                              alt={r.title}
                              sx={{
                                width: 44,
                                height: 44,
                                borderRadius: 1.5,
                                objectFit: "cover",
                                border: "1px solid rgba(15,50,65,0.10)",
                              }}
                            />
                          ) : (
                            <Box
                              sx={{
                                width: 44,
                                height: 44,
                                borderRadius: 1.5,
                                backgroundColor: "#F1F5F9",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                color: "#94A3B8",
                                fontSize: 10,
                                border: "1px solid rgba(15,50,65,0.08)",
                              }}
                            >
                              —
                            </Box>
                          )}
                        </TableCell>
                        <TableCell
                          sx={{
                            fontWeight: 700,
                            color: textPrimary,
                            maxWidth: 200,
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                          }}
                          title={r.title}
                        >
                          {r.title}
                        </TableCell>
                        <TableCell
                          sx={{
                            maxWidth: 320,
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                          }}
                          title={r.description}
                        >
                          {r.description}
                        </TableCell>
                        <TableCell>{r.sunucuKodu || "Hepsi"}</TableCell>
                        <TableCell>
                          <Box
                            component="span"
                            sx={{
                              px: 1,
                              py: 0.25,
                              borderRadius: 5,
                              backgroundColor: "rgba(4,87,129,0.10)",
                              color: "#045781",
                              fontWeight: 700,
                              fontSize: 10.5,
                              whiteSpace: "nowrap",
                            }}
                          >
                            {userTypeLabel(r.userType)}
                          </Box>
                        </TableCell>
                        <TableCell
                          sx={{
                            whiteSpace: "nowrap",
                            color: textSecondary,
                            fontSize: 11.5,
                          }}
                        >
                          {formatDate(r.createdAt)}
                        </TableCell>
                        <TableCell align="right" sx={{ whiteSpace: "nowrap" }}>
                          <Tooltip title="Görüntüle">
                            <IconButton
                              size="small"
                              onClick={() =>
                                setDescDialog({
                                  open: true,
                                  title: r.title,
                                  desc: r.description,
                                })
                              }
                              sx={{
                                color: "#15803D",
                                "&:hover": {
                                  backgroundColor: "rgba(21,128,61,0.10)",
                                },
                              }}
                            >
                              <ArticleOutlined sx={{ fontSize: 18 }} />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Düzenle">
                            <IconButton
                              size="small"
                              onClick={() =>
                                setEditDialog({ open: true, item: r })
                              }
                              sx={{
                                color: "#045781",
                                "&:hover": {
                                  backgroundColor: "rgba(4,87,129,0.10)",
                                },
                              }}
                            >
                              <EditOutlined sx={{ fontSize: 18 }} />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Sil">
                            <IconButton
                              size="small"
                              onClick={() => handleDelete(r)}
                              sx={{
                                color: "#B91C1C",
                                "&:hover": {
                                  backgroundColor: "rgba(185,28,28,0.10)",
                                },
                              }}
                            >
                              <DeleteOutlineRounded sx={{ fontSize: 18 }} />
                            </IconButton>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </Box>
          </Paper>

          {rows.length > 0 && (
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                flexShrink: 0,
                px: 0.5,
                animation: `${fadeInUp} 0.4s ease-out 0.2s backwards`,
              }}
            >
              <Typography
                sx={{
                  color: "rgba(255,255,255,0.75)",
                  fontSize: 11.5,
                  fontWeight: 600,
                }}
              >
                {Math.min((safePage - 1) * PAGE_SIZE + 1, rows.length)}–
                {Math.min(safePage * PAGE_SIZE, rows.length)} / {rows.length}{" "}
                kayıt
              </Typography>
              <Pagination
                count={totalPages}
                page={safePage}
                onChange={(_, p) => setPage(p)}
                size="small"
                sx={{
                  "& .MuiPaginationItem-root": {
                    color: "#fff",
                    "&.Mui-selected": {
                      backgroundColor: "rgba(255,255,255,0.24)",
                      fontWeight: 800,
                    },
                    "&:hover": { backgroundColor: "rgba(255,255,255,0.14)" },
                  },
                }}
              />
            </Box>
          )}
        </>
      )}

      {/* Dialoglar */}
      <NotificationDescDialog
        open={descDialog.open}
        onClose={() => setDescDialog({ open: false, title: "", desc: "" })}
        title={descDialog.title}
        desc={descDialog.desc}
      />

      <NotificationEditDialog
        open={editDialog.open}
        item={editDialog.item}
        sunucuKoduList={sunucuKoduList}
        onClose={() => setEditDialog({ open: false, item: null })}
        onSaved={() => {
          setSnack({
            open: true,
            msg: "Duyuru güncellendi.",
            severity: "success",
          });
          loadData(true);
        }}
      />

      <Snackbar
        open={snack.open}
        autoHideDuration={3000}
        onClose={() => setSnack((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          severity={snack.severity}
          onClose={() => setSnack((s) => ({ ...s, open: false }))}
          sx={{ borderRadius: 2 }}
        >
          {snack.msg}
        </Alert>
      </Snackbar>
    </Box>
  );
}
